import { useState } from 'react';

export default function ITConsultingPage() {
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showCustomerToolsDropdown, setShowCustomerToolsDropdown] =
    useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.currentTarget;
      const formDataObj = new FormData(form);

      // Textarea karakter sınırı kontrolü
      const message = formDataObj.get('message') as string;
      if (message && message.length > 500) {
        setSubmitStatus({
          type: 'error',
          message: 'Mesaj 500 karakterden uzun olamaz.',
        });
        setIsSubmitting(false);
        return;
      }

      // Supabase Edge Function'a gönder
      const response = await fetch(
        'https://lbvcnepwlojmojlxmhau.supabase.co/functions/v1/send-contact-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fullName: formDataObj.get('fullName'),
            phone: formDataObj.get('phone'),
            email: formDataObj.get('email'),
            company: formDataObj.get('company'),
            message: formDataObj.get('message'),
          }),
        }
      );

      let result: any;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Sunucudan geçerli bir yanıt alınamadı.');
      }

      if (result?.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Talebiniz başarıyla gönderildi. En kısa sürede sizinle iletişime geçeceğiz.',
        });
        form.reset();
        setTimeout(() => {
          setShowCallbackModal(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error(result?.error || 'Form gönderimi başarısız');
      }
    } catch (error) {
      console.error('Form submit error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setShowServicesDropdown(false);
    setShowCustomerToolsDropdown(false);
  };

  const consultingServices = [
    {
      icon: 'ri-pie-chart-line',
      title: 'IT Stratejisi ve Planlama',
      description:
        'İşletmenizin IT vizyonunu oluşturuyor, teknoloji yol haritanızı planlıyoruz. Mevcut durumunuzu analiz eder, gelecekteki ihtiyaçlarınızı öngörür ve bütçe planlamanızı destekleriz.',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Dijital Dönüşüm Danışmanlığı',
      description:
        'Geleneksel iş süreçlerinizi dijital ortama taşımanızda rehberlik ederiz. Süreç optimizasyonu, otomasyon çözümleri ve dijital kültür oluşturma konularında uzman desteği sağlarız.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Siber Güvenlik Danışmanlığı',
      description:
        'Güvenlik açıklarınızı tespit eder, risk analizleri yaparız. ISO 27001, KVKV uyumluluk süreçlerinde danışmanlık sağlar, güvenlik politikalarınızı geliştiririz.',
    },
    {
      icon: 'ri-cloud-line',
      title: 'Bulut Migrasyonu Danışmanlığı',
      description:
        'Mevcut sistemlerinizi buluta taşıma stratejinizi belirler, en uygun bulut platformunu seçmenize yardımcı oluruz. Hibrit bulut mimarileri tasarlar ve geçiş sürecinizi yönetiriz.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'IT Performans Optimizasyonu',
      description:
        'Mevcut IT altyapınızın performansını analiz eder, darboğazları tespit ederiz. Sistem kaynaklarının daha verimli kullanılması için öneriler geliştir ve uygularız.',
    },
    {
      icon: 'ri-team-line',
      title: 'IT Yönetimi ve Organizasyon',
      description:
        'IT departmanınızın organizasyon yapısını optimize eder, ITIL süreçlerini hayata geçiririz. Personel yetkinlik analizi yapar ve eğitim planları oluştururuz.',
    },
  ];

  const consultingProcess = [
    {
      step: '01',
      title: 'Mevcut Durum Analizi',
      description:
        'İşletmenizin mevcut IT altyapısını, süreçlerini ve kaynaklarını detaylı şekilde analiz ederiz. Güçlü yönleri ve iyileştirme alanlarını belirleriz.',
    },
    {
      step: '02',
      title: 'İhtiyaç Tespiti',
      description:
        'İş hedeflerinizi dinler, teknoloji ihtiyaçlarınızı detaylı şekilde belirleriz. Kısa, orta ve uzun vadeli hedeflerinizi analiz ederiz.',
    },
    {
      step: '03',
      title: 'Strateji Geliştirme',
      description:
        'İhtiyacınıza özel teknoloji stratejisi geliştiririz. ROI analizi yaparak en uygun çözümleri belirler ve önceliklendiririz.',
    },
    {
      step: '04',
      title: 'Yol Haritası Oluşturma',
      description:
        'Detaylı uygulama planı hazırlar, zaman çizelgesi oluştururuz. Her adımın bütçe, kaynak ve risk analizini yaparız.',
    },
    {
      step: '05',
      title: 'Uygulama Desteği',
      description:
        'Planlanan projelerin hayata geçirilmesinde aktif destek sağlarız. Proje yönetimi ve teknik uygulama süreçlerinde yanınızda oluruz.',
    },
    {
      step: '06',
      title: 'İzleme ve Optimizasyon',
      description:
        'Uygulanan çözümlerin performansını sürekli izler, gerekli optimizasyonları yaparız. Düzenli raporlama ile sonuçları takip ederiz.',
    },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-award-line',
      title: '15+ Yıl Deneyim',
      description:
        'IT sektöründe biriken deneyimimizle her ölçekten işletmeye danışmanlık hizmeti veriyoruz.',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Uzman Kadro',
      description:
        'Sertifikalı IT uzmanları ve danışmanlarımızla en güncel teknolojileri takip ediyoruz.',
    },
    {
      icon: 'ri-target-line',
      title: 'Sonuç Odaklı Yaklaşım',
      description:
        'Teorik bilgiden ziyade, uygulanabilir ve ölçülebilir sonuçlar üretmeye odaklanıyoruz.',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Güvenilir Partner',
      description:
        'Microsoft, Fortinet ve Veeam iş ortaklarımızla endüstri standardında çözümler sunuyoruz.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-gray-900 sticky top-0 z-50 shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-8">
              <a href="/" className="flex items-center space-x-3 cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg text-gray-900">
                  Nimbus Bulut Bilişim Teknoloji ve Mühendislik
                </div>
              </a>
              <nav className="hidden lg:flex space-x-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowServicesDropdown(!showServicesDropdown);
                      setShowCustomerToolsDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>IT Hizmetleri</span>
                    <i
                      className={`ri-arrow-${showServicesDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showServicesDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-[1000px] bg-white rounded-lg shadow-lg border border-gray-200 py-6 z-50">
                      <div className="grid grid-cols-3 gap-8 px-8">
                        {/* IT Destek Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            IT Destek Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-support');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Destek & Bakım Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">7/24 IT destek ve bakım hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-consultinging');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                            >
                              <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Profesyonel danışmanlık</div>
                            </button>
                          </div>
                        </div>

                        {/* Bulut Bilişim Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Bulut Bilişim Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/virtual-server');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Kurumsal bulut sunucu hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/managed-it');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Yönetilen IT Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Tüm yönetim hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/migration');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Sistem Taşıma ve Geçiş Hizmetleri (Migration)</div>
                              <div className="text-sm text-gray-500 mt-1">Sunucu taşıma hizmetleri</div>
                            </button>
                          </div>
                        </div>

                        {/* Microsoft 365 Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Microsoft 365 Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/microsoft-365');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                              <div className="text-sm text-gray-500 mt-1">Azure Expert hizmetleri</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Başarı Hikayeleri
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/about')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Hakkımızda
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  SSS
                </button>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-4 ml-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowCustomerToolsDropdown(!showCustomerToolsDropdown);
                      setShowServicesDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>Müşteri Araçları</span>
                    <i
                      className={`ri-arrow-${showCustomerToolsDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showCustomerToolsDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/')}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                      >
                        <div className="font-medium">Müşteri Portalı</div>
                        <div className="text-sm text-gray-500">
                          Fatura ve destek yönetimi
                        </div>
                      </button>
                      <a
                        href="mailto:destek@nimbusbilisim.com"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                      >
                        <div className="font-medium">7/24 Destek Hattı</div>
                        <div className="text-sm text-gray-500">
                          destek@nimbusbilisim.com
                        </div>
                      </a>
                      <a
                        href="https://rustdesk.com/download"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                      >
                        <div className="font-medium">RustDesk İndir</div>
                        <div className="text-sm text-gray-500">
                          Uzaktan destek programı
                        </div>
                      </a>
                      <button
                        onClick={() => {
                          setShowCallbackModal(true);
                          setShowCustomerToolsDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                      >
                        <div className="font-medium">Geri Arama Talebi</div>
                        <div className="text-sm text-gray-500">
                          Uzmanlarımızla görüşün
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowCallbackModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                İletişim
              </button>
              <a
                href="https://www.linkedin.com/company/nimbus-bulut-bili%C5%9Fim-teknoloji-yaz%C4%B1l%C4%B1m-ve-m%C3%BChendislik-a-%C5%9F/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-2xl"></i>
              </a>
              <button
                onClick={() => setShowMobileMenu(true)}
                className="lg:hidden text-gray-700 cursor-pointer"
              >
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Click outside to close dropdowns */}
      {(showServicesDropdown || showCustomerToolsDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowServicesDropdown(false);
            setShowCustomerToolsDropdown(false);
          }}
        ></div>
      )}

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileMenu(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-gray-900">Menü</h3>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <nav className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    IT Hizmetleri
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/it-support');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">
                        IT Destek & Bakım Hizmetleri
                      </div>
                      <div className="text-sm text-gray-500">
                        7/24 IT destek ve bakım hizmetleri (uzaktan & yerinde)
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/it-consulting');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                    >
                      <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                      <div className="text-sm text-blue-500">
                        Projeleriniz için profesyonel danışmanlık
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/virtual-server');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        İşletmeniz için tam kapsamlı kurumsal bulut sunucu
                        hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/managed-it');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Yönetilen IT Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Tek bir noktadan ihtiyacınız olan tüm yönetim
                        hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/microsoft-365');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                      <div className="text-sm text-gray-500">
                        Azure projeleriniz için Azure Expert hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/migration');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Sistem Taşıma ve Geçiş</div>
                      <div className="text-sm text-gray-500">
                        Sunucu taşıma sürecinizde tam kontrol
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Şirket
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Başarı Hikayeleri
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/about');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Hakkımızda
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      SSS
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Müşteri Araçları
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Müşteri Portalı</div>
                      <div className="text-sm text-gray-500">
                        Fatura ve destek yönetimi
                      </div>
                    </button>
                    <a
                      href="mailto:destek@nimbusbilisim.com"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">7/24 Destek Hattı</div>
                      <div className="text-sm text-gray-500">
                        destek@nimbusbilisim.com
                      </div>
                    </a>
                    <a
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">RustDesk İndir</div>
                      <div className="text-sm text-gray-500">
                        Uzaktan destek programı
                      </div>
                    </a>
                    <button
                      onClick={() => {
                        setShowCallbackModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Geri Arama Talebi</div>
                      <div className="text-sm text-gray-500">
                        Uzmanlarımızla görüşün
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={() => {
                      setShowCallbackModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    İletişim
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/9839a7531217cc6933cb2d7c6648cff5.jpg')`,
            animation: 'slowPan 5s ease-in-out infinite alternate'
          }}
        ></div>
        <style>{`
          @keyframes slowPan {
            0% {
              transform: scale(1) translateX(-2%);
            }
            100% {
              transform: scale(1.1) translateX(2%);
            }
          }
        `}</style>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-blue-400 text-lg mb-4">
              Stratejik IT Danışmanlığı ile İşinizi Geleceğe Taşıyın
            </p>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              IT Danışmanlık Hizmetleri
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              İşletmenizin teknoloji hedeflerini belirlemek, dijital dönüşüm 
              stratejinizi geliştirmek ve IT yatırımlarınızın geri dönüşünü 
              maksimize etmek için uzman danışmanlık desteği alın.
            </p>
            <button
              onClick={() => setShowCallbackModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Ücretsiz Danışmanlık Alın</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Consulting Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              IT Danışmanlık Hizmet Alanlarımız
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nimbus Bilişim olarak, işletmenizin her seviyesindeki IT ihtiyaçlarına 
              yönelik kapsamlı danışmanlık hizmetleri sunuyoruz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultingServices.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className={`${service.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              IT Danışmanlık Sürecimiz
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Yapılandırılmış ve kanıtlanmış metodolojimizle, işletmenizin IT 
              dönüşümünü baştan sona yönetiyoruz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultingProcess.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-6 relative"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {step.step}
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Neden Nimbus Bilişim IT Danışmanlığı?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Deneyimli ekibimiz ve kanıtlanmış metodolojilerimizle, 
              işletmenizin IT hedeflerine ulaşmasını sağlıyoruz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((advantage, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className={`${advantage.icon} text-blue-600 text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            IT Stratejinizi Belirlemek İçin Hemen Başlayın
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Uzman danışmanlarımızla görüşerek, işletmenizin IT ihtiyaçlarını 
            değerlendirin ve özelleştirilmiş çözüm önerilerimizi keşfedin.
          </p>
          <button
            onClick={() => setShowCallbackModal(true)}
            className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 mx-auto whitespace-nowrap cursor-pointer"
          >
            <span>Ücretsiz Danışmanlık Talep Edin</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-gray-900 py-12 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg">Nimbus Bulut Bilişim Teknoloji ve Mühendislik</div>
              </div>
              <p className="text-gray-600 mb-4">
                Türkiye'nin güvenilir IT çözüm ortağı. İşletmenizin teknoloji
                ihtiyaçlarında yanımızdayız.
              </p>
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Hizmetler</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/virtual-server')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Sanal Sunucu
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/microsoft-365')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Microsoft 365
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/it-support')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    IT Destek
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/services/it-consulting')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    IT Danışmanlık
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Şirket</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/about')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Hakkımızda
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Başarı Hikayeleri
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    SSS
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Müşteri Portalı
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@nimbusbilisim.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>İzmir, Türkiye</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-10 pt-10 text-center text-gray-600">
            <p>
              © 2025 Nimbus Bulut Bilişim Teknoloji ve Mühendislik. Tüm hakları saklıdır. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-blue-600 transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Callback Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                    alt="Nimbus Bilişim Logo"
                    className="h-12 w-auto"
                  />
                  <h3 className="text-2xl font-bold text-gray-900">
                    IT Danışmanlık Talebi
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowCallbackModal(false);
                    setSubmitStatus(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form
                onSubmit={handleFormSubmit}
                data-readdy-form
                id="consultinging-contact-form"
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Telefon numaranız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="E-posta adresiniz"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Şirket *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Şirket adınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Danışmanlık İhtiyacınız
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Hangi konularda danışmanlık ihtiyacınız var? (maksimum 500 karakter)"
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg transition-colors mt-6 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Danışmanlık Talebi Gönder'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
